import React, { useState, useRef } from "react";
import { Upload, FileText, Download, Loader2, Sparkles, CheckCircle2, Circle } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { toast } from "../../lib/utils";
import { callClaude } from "../../lib/ai";
import mammoth from "mammoth/mammoth.browser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PptxGenJS from "pptxgenjs";
import { Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType } from "docx";
import { saveAs } from "file-saver";

/**
 * Documents tab — end-to-end SAP Activate artefact pipeline.
 *
 * Flow:
 *   1. User uploads / pastes a Business Requirements Document (BRD).
 *   2. Extracted text is stored at doc.documents.brdText.
 *   3. For each catalog entry, a "Generate" button asks Claude to produce
 *      the artefact from BRD + project state. Content is stored at
 *      doc.documents.artefacts[docKey].
 *   4. Download in PDF / Word / PowerPoint via existing export libraries.
 *
 * All artefacts persist server-side via autosave, so the pipeline is stateful.
 */

const CATALOG = [
  { phase: "1. Prepare", key: "charter",              title: "Project Charter" },
  { phase: "1. Prepare", key: "pmp",                  title: "Project Management Plan (scope · schedule · RAID · comms · governance)" },
  { phase: "1. Prepare", key: "resourcePlan",         title: "Resource / Staffing Plan" },
  { phase: "1. Prepare", key: "kickoff",              title: "Kickoff Deck" },

  { phase: "2. Explore (Design)", key: "fitGap",      title: "Fit-to-Standard / Fit-Gap Workshop Document" },
  { phase: "2. Explore (Design)", key: "bpdd",        title: "Business Process Design Document (BPDD) — per process area" },
  { phase: "2. Explore (Design)", key: "rtm",         title: "Requirements Traceability Matrix (RTM)" },
  { phase: "2. Explore (Design)", key: "wricef",      title: "WRICEF / Development Inventory List" },
  { phase: "2. Explore (Design)", key: "sad",         title: "Solution Architecture Document" },
  { phase: "2. Explore (Design)", key: "dataStrat",   title: "Data Migration Strategy" },
  { phase: "2. Explore (Design)", key: "intStrat",    title: "Integration Strategy / Interface Register" },
  { phase: "2. Explore (Design)", key: "secConcept",  title: "Security &amp; Authorization Concept" },
  { phase: "2. Explore (Design)", key: "testStrat",   title: "Testing Strategy" },
  { phase: "2. Explore (Design)", key: "ocmStrat",    title: "Change Management &amp; Training Strategy" },
  { phase: "2. Explore (Design)", key: "designSignoff", title: "Baseline / Design Sign-off" },

  { phase: "3. Realize (Build) — Object level", key: "osd",     title: "Object Specification Document (OSD)" },
  { phase: "3. Realize (Build) — Object level", key: "ts",      title: "Technical Specification (TS)" },
  { phase: "3. Realize (Build) — Object level", key: "unitPlan",title: "Unit Test Plan → Results" },
  { phase: "3. Realize (Build) — Object level", key: "codeRev", title: "Code Review / Clean-Core Compliance Checklist" },
  { phase: "3. Realize (Build) — Object level", key: "fut",     title: "Functional Unit Test (FUT) Sign-off" },

  { phase: "3b. Realize (Project level)", key: "configDoc",     title: "Configuration Documentation" },
  { phase: "3b. Realize (Project level)", key: "dataMap",       title: "Data Migration Mapping &amp; Load Test Results" },
  { phase: "3b. Realize (Project level)", key: "sit",           title: "SIT Scripts → Results" },
  { phase: "3b. Realize (Project level)", key: "regression",    title: "Regression Test Scripts (Brownfield)" },
  { phase: "3b. Realize (Project level)", key: "uat",           title: "UAT Scripts → Results &amp; Business Sign-off" },
  { phase: "3b. Realize (Project level)", key: "training",      title: "Training Materials / Work Instructions" },

  { phase: "4. Deploy",  key: "cutoverPlan",  title: "Cutover Plan (detailed runbook · sequence · owners · timings)" },
  { phase: "4. Deploy",  key: "goNoGo",       title: "Go / No-Go Decision Checklist" },
  { phase: "4. Deploy",  key: "transports",   title: "Transport Release Plan" },
  { phase: "4. Deploy",  key: "dmResults",    title: "Data Migration Execution Results" },
  { phase: "4. Deploy",  key: "hypercarePlan",title: "Hypercare Plan" },
  { phase: "4. Deploy",  key: "goLiveComms",  title: "Go-Live Announcement / Comms" },

  { phase: "5. Run (Post Go-Live)", key: "hcLog",      title: "Hypercare Issue Log" },
  { phase: "5. Run (Post Go-Live)", key: "runDoc",     title: "Operational / Run Documentation" },
  { phase: "5. Run (Post Go-Live)", key: "ktDoc",      title: "Knowledge Transfer Documents" },
  { phase: "5. Run (Post Go-Live)", key: "lessons",    title: "Lessons Learned" },
  { phase: "5. Run (Post Go-Live)", key: "closure",    title: "Project Closure Document" },
];

/* ── File-to-text extraction ───────────────────────────────────────────── */
async function fileToText(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".txt") || name.endsWith(".md") || file.type.startsWith("text/")) {
    return await file.text();
  }
  if (name.endsWith(".docx")) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return r.value || "";
  }
  if (name.endsWith(".pdf")) {
    throw new Error("PDF parsing not yet available — please paste the text content manually or save as .docx / .txt");
  }
  // Fallback — try reading as text
  return await file.text();
}

/* ── Prompt builder ────────────────────────────────────────────────────── */
function buildPrompt(entry, brdText, doc) {
  const meta = doc.meta || {};
  const shared = `You are a Senior SAP Delivery Director drafting the "${entry.title}" for the "${meta.name}" project.
Project meta: phase=${meta.phase}, sponsor=${meta.sponsor || "TBD"}, PM=${meta.pm || "TBD"}, budget=${meta.budget || "TBD"}.
Delivery variant: ${(doc.governance && doc.governance.variant) || "Greenfield"}.

Business Requirements Document (source of truth — quote it faithfully):
"""${(brdText || "").slice(0, 12000)}"""

Produce a professional, board-ready document in Markdown with these conventions:
- Start with a clear H1 title
- Use H2 sections and bulleted / numbered lists where structure helps
- Include specific references from the BRD (do not invent data)
- Keep the total under 900 words unless the artefact type requires more
- End with an "Approvers" section (name / role / date placeholders)`;
  return `${shared}\n\nGenerate the full "${entry.title}" now.`;
}

/* ── Multi-format download ─────────────────────────────────────────────── */
function downloadPDF(title, md) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  pdf.setFontSize(16); pdf.text(title, 40, 60);
  pdf.setFontSize(10);
  const lines = pdf.splitTextToSize(md, 515);
  pdf.text(lines, 40, 90);
  pdf.save(`${title}.pdf`);
}

async function downloadWord(title, md) {
  const paragraphs = [
    new Paragraph({ text: title, heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 240 } }),
  ];
  md.split("\n").forEach(line => {
    if (line.startsWith("# ")) paragraphs.push(new Paragraph({ text: line.slice(2), heading: HeadingLevel.HEADING_1 }));
    else if (line.startsWith("## ")) paragraphs.push(new Paragraph({ text: line.slice(3), heading: HeadingLevel.HEADING_2 }));
    else if (line.startsWith("### ")) paragraphs.push(new Paragraph({ text: line.slice(4), heading: HeadingLevel.HEADING_3 }));
    else if (line.trim().startsWith("- ") || line.trim().startsWith("* ")) paragraphs.push(new Paragraph({ text: line.replace(/^\s*[-*]\s+/, "• ") }));
    else paragraphs.push(new Paragraph({ children: [new TextRun(line || " ")] }));
  });
  const d = new Document({ sections: [{ children: paragraphs }] });
  const blob = await Packer.toBlob(d);
  saveAs(blob, `${title}.docx`);
}

async function downloadPPT(title, md) {
  const ppt = new PptxGenJS();
  ppt.layout = "LAYOUT_WIDE";
  // Slice by H2 headings so each section becomes a slide
  const sections = [];
  let currTitle = title;
  let buf = [];
  md.split("\n").forEach(line => {
    if (line.startsWith("## ")) {
      if (buf.length) sections.push({ title: currTitle, body: buf.join("\n") });
      currTitle = line.slice(3);
      buf = [];
    } else {
      buf.push(line);
    }
  });
  if (buf.length) sections.push({ title: currTitle, body: buf.join("\n") });

  sections.slice(0, 30).forEach(s => {
    const slide = ppt.addSlide();
    slide.addText(s.title, { x: 0.5, y: 0.3, w: 12.3, h: 0.7, fontSize: 26, bold: true, color: "0369A1" });
    slide.addText(s.body || "(empty)", { x: 0.5, y: 1.1, w: 12.3, h: 5.8, fontSize: 12, color: "1E293B", valign: "top" });
  });
  await ppt.writeFile({ fileName: `${title}.pptx` });
}

/* ── Component ─────────────────────────────────────────────────────────── */
export default function Documents() {
  const { doc, mutate, readOnly } = useProject();
  const documents = doc?.documents || {};
  const brdText = documents.brdText || "";
  const artefacts = documents.artefacts || {};
  const fileRef = useRef(null);
  const [busyKey, setBusyKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const setBrd = (t, fileName) => mutate(d => ({ ...d, documents: { ...(d.documents || {}), brdText: t, brdFile: fileName || (d.documents || {}).brdFile } }));
  const setArtefact = (k, v) => mutate(d => ({ ...d, documents: { ...(d.documents || {}), artefacts: { ...((d.documents || {}).artefacts || {}), [k]: { ...(((d.documents || {}).artefacts || {})[k] || {}), ...v } } } }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const t = await fileToText(file);
      setBrd(t, file.name);
      toast(`BRD ingested from ${file.name} · ${t.length.toLocaleString()} chars`);
    } catch (err) {
      toast(err.message || "Failed to read file");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generate = async (entry) => {
    if (!brdText.trim()) return toast("Upload or paste the BRD text first");
    setBusyKey(entry.key);
    try {
      const md = await callClaude(buildPrompt(entry, brdText, doc));
      setArtefact(entry.key, { content: md, generatedAt: new Date().toISOString() });
      toast(`${entry.title} generated`);
    } catch (err) {
      toast(err.message || "Generation failed");
    } finally {
      setBusyKey(null);
    }
  };

  const generateAll = async () => {
    if (!brdText.trim()) return toast("Upload or paste the BRD text first");
    if (!window.confirm(`Generate all ${CATALOG.length} artefacts? This will make ~${CATALOG.length} Claude calls and may take 3-5 minutes.`)) return;
    for (const entry of CATALOG) {
      setBusyKey(entry.key);
      try {
        const md = await callClaude(buildPrompt(entry, brdText, doc));
        setArtefact(entry.key, { content: md, generatedAt: new Date().toISOString() });
      } catch (err) {
        toast(`${entry.title}: ${err.message}`);
        break;
      }
    }
    setBusyKey(null);
    toast("Batch generation complete");
  };

  // Group catalog by phase
  const byPhase = CATALOG.reduce((acc, e) => { (acc[e.phase] = acc[e.phase] || []).push(e); return acc; }, {});
  const generated = Object.keys(artefacts).filter(k => artefacts[k]?.content).length;

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">SAP Activate · document pipeline</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Documents</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Upload a Business Requirements Document. Harmonizer generates the full sequenced Activate artefact set (Charter → OSD → TS → Cutover → Closure) and lets you download each in PDF, Word or PowerPoint.</p>
      </div>

      {/* BRD upload */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Upload size={14} className="text-blue-600"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Step 1 — ingest the BRD</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept=".txt,.md,.docx" onChange={onFile} className="hidden" data-testid="brd-file" />
          <button className="btn btn-primary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} data-testid="brd-upload">
            {uploading ? <><Loader2 size={12} className="animate-spin"/> Reading…</> : <><Upload size={12}/> Upload BRD (.docx / .txt)</>}
          </button>
          {documents.brdFile && <span className="font-mono text-[10px] text-zinc-600">📄 {documents.brdFile} · {brdText.length.toLocaleString()} chars</span>}
          <span className="text-xs text-zinc-400">or paste below</span>
        </div>
        <textarea
          className="fld mt-3 min-h-[120px] font-mono text-xs"
          placeholder="Paste your Business Requirements Document text here…"
          value={brdText}
          onChange={e => setBrd(e.target.value)}
          data-testid="brd-textarea"
          readOnly={readOnly}
        />
      </div>

      {/* Batch */}
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Step 2 — generate artefacts · {generated} / {CATALOG.length} ready</div>
        <button className="btn btn-secondary btn-sm" onClick={generateAll} disabled={!brdText || busyKey || readOnly} data-testid="brd-generate-all">
          {busyKey ? <><Loader2 size={12} className="animate-spin"/> Generating…</> : <><Sparkles size={12}/> Generate all</>}
        </button>
      </div>

      {/* Catalog */}
      {Object.entries(byPhase).map(([phase, items]) => (
        <div key={phase} className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-900 mb-2">{phase}</div>
          <div className="card p-0 overflow-hidden">
            {items.map(entry => {
              const art = artefacts[entry.key];
              const ready = !!art?.content;
              const busy = busyKey === entry.key;
              const isOpen = expanded === entry.key;
              return (
                <div key={entry.key} className="border-b border-[var(--border)] last:border-0">
                  <div className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50">
                    {ready ? <CheckCircle2 size={14} className="text-green-600 shrink-0"/> : <Circle size={14} className="text-zinc-300 shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900 truncate" dangerouslySetInnerHTML={{ __html: entry.title }}/>
                      {ready && art.generatedAt && <div className="font-mono text-[10px] text-zinc-400">Generated {new Date(art.generatedAt).toLocaleString()}</div>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button className="btn btn-ghost btn-sm" disabled={!brdText || busy || readOnly} onClick={() => generate(entry)} data-testid={`gen-${entry.key}`}>
                        {busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                        {ready ? "Regenerate" : "Generate"}
                      </button>
                      {ready && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(isOpen ? null : entry.key)} data-testid={`view-${entry.key}`}>
                            <FileText size={12}/> View
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => downloadPDF(entry.title.replace(/<[^>]+>/g, ""), art.content)} data-testid={`pdf-${entry.key}`}><Download size={12}/> PDF</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => downloadWord(entry.title.replace(/<[^>]+>/g, ""), art.content)}><Download size={12}/> Word</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => downloadPPT(entry.title.replace(/<[^>]+>/g, ""), art.content)}><Download size={12}/> PPT</button>
                        </>
                      )}
                    </div>
                  </div>
                  {isOpen && ready && (
                    <div className="px-4 py-3 bg-zinc-50 border-t border-[var(--border)]">
                      <pre className="whitespace-pre-wrap font-mono text-[11px] text-zinc-800 leading-relaxed max-h-[420px] overflow-y-auto">{art.content}</pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}
